import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Close as CloseIcon,
  Cancel as CancelIcon,
  Block as BlockIcon
} from '@mui/icons-material';

interface LoanRejectDialogProps {
  openRejectModal: boolean;
  rejectionReason: string;
  setRejectionReason: (reason: string) => void;
  rejectionType?: string;
  setRejectionType?: (type: string) => void;
  handleReject: () => void;
  statusChanging: boolean;
  handleCloseRejectModal: () => void;
  loanId?: string;
  applicantName?: string;
}

const commonRejectionReasons = [
  "Insufficient income documentation",
  "Poor credit history",
  "High debt-to-income ratio",
  "Incomplete application",
  "Does not meet eligibility criteria",
  "Collateral insufficient",
  "Other (specify in comments)"
];

const LoanRejectDialog = ({
  openRejectModal,
  rejectionReason,
  setRejectionReason,
  rejectionType = "other",
  setRejectionType,
  handleReject,
  statusChanging,
  handleCloseRejectModal,
  loanId,
  applicantName
}: LoanRejectDialogProps) => {
  const [selectedCommonReason, setSelectedCommonReason] = React.useState("");

  const handleCommonReasonSelect = (reason: string) => {
    setSelectedCommonReason(reason);
    if (reason !== "Other (specify in comments)") {
      setRejectionReason(reason);
    } else {
      setRejectionReason("");
    }
  };

  const isFormValid = rejectionReason.trim().length >= 10;

  return (
    <Dialog 
      open={openRejectModal} 
      onClose={handleCloseRejectModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(to bottom, #fef7f7, #fff)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(45deg, #ffebee, #fce4ec)'
      }}>
        <BlockIcon color="error" sx={{ mr: 1 }} />
        <Typography variant="h6" component="span" color="error.main">
          Reject Loan Application
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleCloseRejectModal}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'grey.500',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, pb: 1 }}>
        {loanId && applicantName && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Rejecting application <strong>#{loanId}</strong> for <strong>{applicantName}</strong>
          </Alert>
        )}
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please provide a detailed reason for rejection. This will be communicated to the applicant.
        </Alert>

        {setRejectionType && (
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <FormLabel component="legend">Rejection Type</FormLabel>
            <RadioGroup
              row
              value={rejectionType}
              onChange={(e) => setRejectionType(e.target.value)}
            >
              <FormControlLabel value="temporary" control={<Radio />} label="Temporary" />
              <FormControlLabel value="permanent" control={<Radio />} label="Permanent" />
            </RadioGroup>
          </FormControl>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Common Rejection Reasons:
        </Typography>
        
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {commonRejectionReasons.map((reason) => (
            <Button
              key={reason}
              variant={selectedCommonReason === reason ? "contained" : "outlined"}
              size="small"
              color={selectedCommonReason === reason ? "error" : "inherit"}
              onClick={() => handleCommonReasonSelect(reason)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.75rem',
                mb: 0.5,
                backgroundColor: selectedCommonReason === reason ? 'error.light' : 'transparent',
                '&:hover': {
                  backgroundColor: selectedCommonReason === reason ? 'error.main' : 'grey.100',
                }
              }}
            >
              {reason}
            </Button>
          ))}
        </Box>

        <TextField
          label="Rejection Reason *"
          placeholder="Please provide a detailed explanation for rejecting this loan application..."
          fullWidth
          multiline
          rows={4}
          value={rejectionReason}
          onChange={(e) => {
            setRejectionReason(e.target.value);
            if (e.target.value && selectedCommonReason === "Other (specify in comments)") {
              setSelectedCommonReason("");
            }
          }}
          variant="outlined"
          autoFocus
          error={rejectionReason.length > 0 && !isFormValid}
          helperText={
            rejectionReason.length > 0 && !isFormValid 
              ? "Reason must be at least 10 characters long" 
              : `${rejectionReason.length}/500 characters`
          }
          inputProps={{ maxLength: 500 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
            }
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button 
          onClick={handleCloseRejectModal} 
          color="inherit"
          disabled={statusChanging}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleReject}
          color="error"
          variant="contained"
          disabled={statusChanging || !isFormValid}
          startIcon={statusChanging ? <CircularProgress size={16} /> : <CancelIcon />}
          sx={{
            borderRadius: 1,
            px: 2,
            background: 'linear-gradient(45deg, #f44336, #d32f2f)',
            '&:hover': {
              background: 'linear-gradient(45deg, #d32f2f, #c62828)',
            }
          }}
        >
          {statusChanging ? 'Rejecting...' : 'Confirm Rejection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanRejectDialog;